import { getUser, authenticatedFetch } from './auth';

/**
 * Generate a device fingerprint based on browser/device characteristics
 */
export function generateDeviceFingerprint(): string {
  const nav = navigator;
  const screen = window.screen;

  const components = [
    nav.userAgent,
    nav.language,
    screen.colorDepth,
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset(),
    !!window.sessionStorage,
    !!window.localStorage,
  ];

  const fingerprint = components.join('|');

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }

  return Math.abs(hash).toString(36);
}

/**
 * Check if current device is trusted
 * @param userId - Optional user ID (use this during login before session is saved)
 */
export async function isDeviceTrusted(userId?: string): Promise<boolean> {
  const fingerprint = generateDeviceFingerprint();

  try {
    // Use provided userId or get from localStorage
    let userIdToCheck = userId;
    if (!userIdToCheck) {
      const user = getUser();
      if (!user || !user.id) {
        console.error('No user session found');
        return false;
      }
      userIdToCheck = user.id;
    }

    const response = await fetch('/api/auth/check-trusted-device', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        device_fingerprint: fingerprint,
        user_id: userIdToCheck,
      }),
    });

    if (!response.ok) {
      console.error('Error checking trusted device:', response.statusText);
      return false;
    }

    const data = await response.json();
    return data?.trusted || false;
  } catch (error) {
    console.error('Error checking trusted device:', error);
    return false;
  }
}

/**
 * Send device verification code
 * @param deviceName - Name for the device
 * @param userId - Optional user ID (use this during login before session is saved)
 */
export async function sendDeviceVerification(deviceName: string, userId?: string): Promise<void> {
  const fingerprint = generateDeviceFingerprint();

  // Use provided userId or get from localStorage
  let userIdToUse = userId;
  if (!userIdToUse) {
    const user = getUser();
    if (!user || !user.id) {
      throw new Error('No user session found');
    }
    userIdToUse = user.id;
  }

  const response = await fetch('/api/auth/send-device-verification', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      device_fingerprint: fingerprint,
      device_name: deviceName,
      user_id: userIdToUse,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Kunne ikke sende verifiseringskode');
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Kunne ikke sende verifiseringskode');
  }

  console.log('✅ Verifiseringskode sendt');
}

/**
 * Verify device with code
 * @param code - Verification code
 * @param deviceName - Name for the device
 * @param userId - Optional user ID (use this during login before session is saved)
 */
export async function verifyDevice(code: string, deviceName: string, userId?: string): Promise<boolean> {
  const fingerprint = generateDeviceFingerprint();

  // Use provided userId or get from localStorage
  let userIdToUse = userId;
  if (!userIdToUse) {
    const user = getUser();
    if (!user || !user.id) {
      throw new Error('No user session found');
    }
    userIdToUse = user.id;
  }

  const response = await fetch('/api/auth/verify-device', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      device_fingerprint: fingerprint,
      device_name: deviceName,
      code: code,
      user_id: userIdToUse,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Verifisering feilet');
  }

  const data = await response.json();
  if (data.error) {
    throw new Error(data.error);
  }

  return data?.success || false;
}

/**
 * Get list of trusted devices
 */
export async function getTrustedDevices() {
  const user = getUser();
  if (!user || !user.id) throw new Error('Not authenticated');

  const response = await authenticatedFetch('/api/auth/get-trusted-devices', {
    method: 'POST',
    body: JSON.stringify({
      user_id: user.id,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to load devices');
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Failed to load devices');
  }

  return data.devices || [];
}

/**
 * Revoke a trusted device
 */
export async function revokeTrustedDevice(deviceId: string): Promise<void> {
  const user = getUser();
  if (!user || !user.id) throw new Error('Not authenticated');

  const response = await authenticatedFetch('/api/auth/revoke-device', {
    method: 'POST',
    body: JSON.stringify({
      user_id: user.id,
      device_id: deviceId,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to revoke device');
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Failed to revoke device');
  }
}
